/**
 * 2048 PK 自适应战术大师 AI (Adaptive Grandmaster 2048 AI Bot)
 * 核心特性：
 * 1. 严守 2048 高手定角纪律：锁定左下角 (3,0) 主锚点 + 蛇形单调递减链，绝不乱滑 UP 破角
 * 2. 双层博弈推演 (2-Ply Expectimax)：兼顾平滑度、单调性、空格率与精准狙击斩半收益
 * 3. 动态实力与节奏自适应：实时感知玩家得分、最大块与手速，动态调节思考步频与推演深度
 * 4. 完整遵循【优势合n斩n/2、劣势合n斩n、无对应块则完美格挡】规则
 */

import type {
  TileData,
  MoveDirection,
  PKGamePacket,
  AttackPacket,
  GameMode,
  DmcGrade,
} from "../../shared/protocol";
import {
  processLine,
  getRandomEmptySpot,
  isBoardStuck,
  findLastNativeTile,
  Tile,
} from "./boardEngine";

export class BotPlayer {
  public readonly botId: string;
  public readonly botName: string;
  public readonly team: "red" | "blue";

  private tiles: Tile[] = [];
  private nextId = 1000;
  private score = 0;
  private maxTile = 2;
  private moves = 0;
  private merges = 0;
  private styleEnergy = 0;
  private timer: number | null = null;
  private isRunning = false;
  private lockedStuck = false;
  private onPacketCallback: (packet: PKGamePacket) => void;
  private onStuckPendingCallback?: () => void;
  private lastAttackTime = 0;
  private gameMode: GameMode = "classic_2048";

  // 玩家实时遥测数据 (用于动态难度与精准狙击决策)
  private playerScore = 0;
  private playerMaxTile = 2;
  private playerTileCounts: Map<number, number> = new Map();
  private playerLastMoveAt = Date.now();
  private playerAvgIntervalMs = 600;

  constructor(
    onPacket: (packet: PKGamePacket) => void,
    gameMode: GameMode = "classic_2048",
    botId: string = "bot_blue_0",
    botName: string = "AI 战术大师",
    team: "red" | "blue" = "blue",
    onStuckPending?: () => void
  ) {
    this.onPacketCallback = onPacket;
    this.gameMode = gameMode;
    this.botId = botId;
    this.botName = botName;
    this.team = team;
    this.onStuckPendingCallback = onStuckPending;
  }

  public getScore(): number {
    return this.score;
  }

  public getMaxTile(): number {
    return this.maxTile;
  }

  public getStyleGrade(): DmcGrade {
    return this.styleEnergy >= 85
      ? "S"
      : this.styleEnergy >= 60
        ? "A"
        : this.styleEnergy >= 35
          ? "B"
          : this.styleEnergy >= 15
            ? "C"
            : "D";
  }

  public getTiles(): Tile[] {
    return this.tiles;
  }

  public isStuck(): boolean {
    return this.lockedStuck || isBoardStuck(this.tiles);
  }

  public hasPendingEjectTile(): boolean {
    return this.lockedStuck && this.tiles.some((t) => Boolean(t.isPendingEject));
  }

  /**
   * 当队友出现空位时，立刻拔起并发射处于「待发射 (isPendingEject)」状态的最后原生方块，同时解除自身封盘继续行动
   */
  public consumePendingEjectTile(): Tile | null {
    if (!this.lockedStuck) return null;
    const pendingTile = this.tiles.find((t) => Boolean(t.isPendingEject));
    if (!pendingTile) return null;

    this.tiles = this.tiles.filter((t) => t.id !== pendingTile.id);
    for (const t of this.tiles) {
      t.isPendingEject = false;
    }
    this.lockedStuck = false;
    this.recalcMaxTile();
    this.broadcastState();
    if (this.isRunning && this.timer === null) {
      this.scheduleNextMove();
    }
    return pendingTile;
  }

  /**
   * 接收队友发射过来的方块（标记为 isTransferred: true 外来方块）：
   * - 若接收后引发封盘，则根据定理：将本棋盘上最后生成的原生方块（非本次接收的外来块）状态变为「待发射 (isPendingEject)」
   */
  public receiveEjectedTile(val: number): boolean {
    if (this.lockedStuck || isBoardStuck(this.tiles)) return false;
    const spot = getRandomEmptySpot(this.tiles);
    if (!spot) return false;
    const droppedTile: Tile = {
      id: this.nextId++,
      r: spot.r,
      c: spot.c,
      val,
      isNew: true,
      isTransferred: true,
    };
    this.tiles.push(droppedTile);
    this.recalcMaxTile();

    if (isBoardStuck(this.tiles)) {
      this.enterStuckPendingEject();
      return true;
    }

    this.broadcastState();
    return true;
  }

  public setGameMode(mode: GameMode) {
    this.gameMode = mode;
  }

  /**
   * 同步人类玩家的实时盘面与手速遥测，驱动 AI 动态调整节奏与狙击目标
   */
  public syncPlayerTelemetry(playerScore: number, playerMaxTile: number, playerTiles: TileData[]) {
    const now = Date.now();
    const dt = now - this.playerLastMoveAt;
    if (dt > 80 && dt < 5000) {
      this.playerAvgIntervalMs = this.playerAvgIntervalMs * 0.75 + dt * 0.25;
    }
    this.playerLastMoveAt = now;
    this.playerScore = playerScore;
    this.playerMaxTile = playerMaxTile;

    this.playerTileCounts.clear();
    for (const t of playerTiles) {
      this.playerTileCounts.set(t.val, (this.playerTileCounts.get(t.val) || 0) + 1);
    }
  }

  public start() {
    this.reset();
    this.isRunning = true;
    this.scheduleNextMove();
  }

  /**
   * 房主断线顺延后，新房主从现有盘面快照无缝接管 AI 机器人继续行动
   */
  public resumeFromSnapshot(snapshot: {
    tiles: TileData[];
    score: number;
    maxTile: number;
    isStuck?: boolean;
  }) {
    this.stop();
    this.score = snapshot.score || 0;
    this.maxTile = snapshot.maxTile || 2;
    this.tiles = (snapshot.tiles || []).map((t, idx) => ({
      id: t.id || this.nextId + idx,
      r: t.r,
      c: t.c,
      val: t.val,
      isLastNative: t.isLastNative,
      isTransferred: t.isTransferred,
      isPendingEject: t.isPendingEject,
      isObstacle: t.isObstacle,
    }));
    const maxExistingId = this.tiles.reduce((m, t) => Math.max(m, t.id), 1000);
    this.nextId = maxExistingId + 1;

    if (this.tiles.length === 0) {
      this.addRandomTile();
      this.addRandomTile();
    }
    this.recalcMaxTile();
    this.lockedStuck = Boolean(snapshot.isStuck) || isBoardStuck(this.tiles);
    this.isRunning = true;
    this.broadcastState();
    if (!this.lockedStuck) {
      this.scheduleNextMove();
    }
  }

  public stop() {
    this.isRunning = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  public reset() {
    this.stop();
    this.lockedStuck = false;
    this.tiles = [];
    this.score = 0;
    this.maxTile = 2;
    this.moves = 0;
    this.merges = 0;
    this.styleEnergy = 0;
    this.playerScore = 0;
    this.playerMaxTile = 2;
    this.playerTileCounts.clear();
    this.addRandomTile();
    this.addRandomTile();
    this.broadcastState();
  }

  /**
   * 结算玩家向 AI 发射的精准斩半攻击：
   * - 若 AI 已封盘定格，则免疫一切后续攻击，盘面与分数不再改变
   * - 若 AI 盘面存在 targetVal 且触发高华丽度 MISS 闪避，则残影偏转不受伤
   * - 若 AI 盘面存在 targetVal 且未闪避，则将其对半劈开并扣除差额分数
   * - 若 AI 盘面不存在 targetVal，则触发完美格挡防御！
   */
  public receiveAttack(attack: AttackPacket): { hit: boolean; missed?: boolean; text: string } {
    if (this.lockedStuck || !this.isRunning || isBoardStuck(this.tiles)) {
      return { hit: false, text: "已封盘" };
    }
    const targetVal = attack.targetVal || attack.power || 32;

    // 查找 AI 盘面上数值等于 targetVal 的方块（若有多枚，优先劈砍靠近主链核心的那枚）
    const candidates = this.tiles
      .filter((t) => t.val === targetVal)
      .sort((a, b) => b.r * 4 - b.c - (a.r * 4 - a.c));

    const target = candidates[0];
    if (target) {
      if (attack.isMissed) {
        const grade = attack.dodgeGrade || this.getStyleGrade();
        return { hit: false, missed: true, text: `⚡ ${grade}级闪避 MISS!` };
      }
      const oldVal = target.val;
      const newVal = Math.max(2, Math.floor(oldVal / 2));
      target.val = newVal;
      this.score = Math.max(0, this.score - (oldVal - newVal));
      this.recalcMaxTile();
      if (isBoardStuck(this.tiles)) {
        this.enterStuckPendingEject();
      } else {
        this.broadcastState();
      }
      return { hit: true, text: `${oldVal} ➔ ${newVal}` };
    }

    // 盘面无对应数字：防御成功！
    return { hit: false, text: `${targetVal}÷2 格挡!` };
  }

  private recalcMaxTile() {
    let max = 2;
    for (const t of this.tiles) {
      if (t.val > max) max = t.val;
    }
    this.maxTile = max;
  }

  private addRandomTile() {
    const spot = getRandomEmptySpot(this.tiles);
    if (!spot) return;
    for (const t of this.tiles) {
      t.isLastNative = false;
    }
    this.tiles.push({
      id: this.nextId++,
      r: spot.r,
      c: spot.c,
      val: Math.random() < 0.9 ? 2 : 4,
      isNew: true,
      isLastNative: true,
    });
  }

  /**
   * 根据玩家实时水平与比分差距，动态调节 AI 思考步频：
   * - 玩家大幅落后或手速较慢时，AI 放缓至拟真思考节奏 (580~920ms)，给玩家留出翻盘窗口
   * - 玩家领先或高频连滑时，AI 提速至竞技状态 (300~480ms)
   */
  private scheduleNextMove() {
    if (!this.isRunning) return;

    const scoreLead = this.score - this.playerScore;
    let baseDelay = Math.min(850, Math.max(300, this.playerAvgIntervalMs * 0.85));

    if (scoreLead > 600) {
      baseDelay = Math.max(baseDelay, 680);
    } else if (scoreLead > 250) {
      baseDelay = Math.max(baseDelay, 520);
    } else if (scoreLead < -300) {
      baseDelay = Math.min(baseDelay, 340);
    }

    const jitter = (Math.random() - 0.5) * 120;
    const delay = Math.round(Math.max(260, baseDelay + jitter));

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.step();
      if (this.isRunning && !this.lockedStuck && this.timer === null) {
        this.scheduleNextMove();
      }
    }, delay);
  }

  /**
   * 无论何种原因触发封盘，一旦封盘：
   * 1. 将本棋盘上最后生成的原生方块状态标记为「待发射 (isPendingEject = true)」
   * 2. 暂停自身移动并广播状态，同时通知战斗系统：一旦队友有空位立刻发射该方块
   */
  private enterStuckPendingEject() {
    if (this.lockedStuck) return;
    this.lockedStuck = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    for (const t of this.tiles) {
      t.isPendingEject = false;
    }
    const lastNative = findLastNativeTile(this.tiles);
    if (lastNative) {
      lastNative.isPendingEject = true;
    }

    this.broadcastState();
    this.onPacketCallback({
      type: "game_over",
      playerId: this.botId,
      playerName: this.botName,
      finalScore: this.score,
      maxTile: this.maxTile,
      duration: 0,
      cause: "STUCK",
    });
    this.onStuckPendingCallback?.();
  }

  private step() {
    if (this.lockedStuck) return;
    if (isBoardStuck(this.tiles)) {
      this.enterStuckPendingEject();
      return;
    }

    const bestDir = this.findBestMove();
    if (!bestDir) {
      this.enterStuckPendingEject();
      return;
    }

    this.executeMove(bestDir);
  }

  private executeMove(dir: MoveDirection) {
    const isVertical = dir === "UP" || dir === "DOWN";
    const isReverse = dir === "DOWN" || dir === "RIGHT";
    let boardMoved = false;
    let earned = 0;
    const allRemoved: Tile[] = [];
    const allSpawn: Tile[] = [];
    const allMergedVals: number[] = [];

    for (let i = 0; i < 4; i++) {
      const line = this.tiles.filter((t) => (isVertical ? t.c === i : t.r === i));
      const res = processLine(line, isReverse, isVertical, i, () => this.nextId++, () => {});
      if (res.moved) {
        boardMoved = true;
        earned += res.earnedScore;
        allRemoved.push(...res.toRemove);
        allSpawn.push(...res.toSpawn);
        allMergedVals.push(...res.mergedValues);
      }
    }

    if (!boardMoved) return;

    this.moves++;
    this.merges += allMergedVals.length;
    this.score += earned;
    if (allMergedVals.length > 0) {
      const topVal = Math.max(...allMergedVals);
      const gain = Math.min(28, 6 * allMergedVals.length + (topVal >= 128 ? 10 : topVal >= 64 ? 5 : 0));
      this.styleEnergy = Math.min(100, this.styleEnergy * 0.88 + gain);
    } else {
      this.styleEnergy = Math.max(0, this.styleEnergy - 7);
    }
    const removeSet = new Set(allRemoved.map((t) => t.id));
    this.tiles = this.tiles.filter((t) => !removeSet.has(t.id));
    this.tiles.push(...allSpawn);
    this.recalcMaxTile();

    this.addRandomTile();

    // 核心机制：合成 n >= 64 时发出原始合成值 topMerged，由 CombatSystem 按每个受击目标的分差与华丽度结算 AOE
    const now = Date.now();
    const topMerged = allMergedVals.filter((v) => v >= 64).sort((a, b) => b - a)[0];
    if (topMerged && now - this.lastAttackTime >= 2200) {
      this.lastAttackTime = now;

      this.onPacketCallback({
        type: "attack",
        attack: {
          id: `bot-atk-${this.botId}-${now}-${topMerged}`,
          type: "CORROSION_DRAIN",
          senderId: this.botId,
          senderName: this.botName,
          senderTeam: this.team,
          power: topMerged,
          targetVal: topMerged,
          description: `${topMerged}÷2`,
          timestamp: now,
        },
      });
    }

    // 若为极速/标准 2048 模式且 AI 率先合出 2048，则宣告达成
    if (this.gameMode !== "endless" && this.maxTile >= 2048) {
      this.isRunning = false;
      this.broadcastState();
      this.onPacketCallback({
        type: "game_over",
        playerId: this.botId,
        playerName: this.botName,
        winningTeam: this.team,
        finalScore: this.score,
        maxTile: this.maxTile,
        duration: 0,
        cause: "REACHED_2048",
      });
      return;
    }

    // 落子生成新块后立即检测是否触发封盘：一旦封盘则将最后原生方块标记为「待发射」
    if (isBoardStuck(this.tiles)) {
      this.enterStuckPendingEject();
      return;
    }

    this.broadcastState();
  }

  private broadcastState() {
    const tileData: TileData[] = this.tiles.map((t) => ({
      id: t.id,
      r: t.r,
      c: t.c,
      val: t.val,
      isNew: t.isNew,
      isMerged: t.isMerged,
      mergedIds: t.mergedIds,
      isLastNative: t.isLastNative,
      isTransferred: t.isTransferred,
      isPendingEject: t.isPendingEject,
    }));

    for (const t of this.tiles) {
      t.isNew = false;
      t.isMerged = false;
      t.mergedIds = undefined;
    }

    const grade = this.getStyleGrade();

    this.onPacketCallback({
      type: "sync_board",
      playerId: this.botId,
      playerName: this.botName,
      team: this.team,
      tiles: tileData,
      score: this.score,
      maxTile: this.maxTile,
      moves: this.moves,
      merges: this.merges,
      styleGrade: grade,
      styleGauge: Math.round(this.styleEnergy),
      inDanger: this.tiles.length >= 14,
      attackGauge: this.score >= this.playerScore ? 50 : 100,
      isStuck: this.lockedStuck || isBoardStuck(this.tiles),
    });
  }

  /**
   * 具备真实 2048 技巧的启发式双层博弈搜索 (2-Ply Expectimax with Corner Discipline)
   * 技巧准则：
   * 1. 主力方向为 DOWN 与 LEFT，将最大块牢牢钉死在左下角 (3,0)
   * 2. 只要 DOWN / LEFT / RIGHT 任一方向可走，绝不走 UP（防止底排脱离引发破角）
   * 3. 走 RIGHT 前检查底排是否已满或可合并，若走 RIGHT 会导致 (3,0) 最大块右移破角，予以重罚
   */
  private findBestMove(): MoveDirection | null {
    const primaryDirs: MoveDirection[] = ["DOWN", "LEFT", "RIGHT"];
    let bestScore = -Infinity;
    let bestDir: MoveDirection | null = null;

    // 是否启用双层深度推演（当玩家领先或势均力敌时启用 2-Ply，当 AI 大幅领先时用 1-Ply 让步）
    const useTwoPly = this.score - this.playerScore < 500;

    for (const dir of primaryDirs) {
      const sim = this.simulateMove(this.tiles, dir);
      if (!sim.moved) continue;

      let totalVal = this.evaluateBoard(sim.tiles, sim.mergedValues);

      // 严防走 RIGHT 导致左下角 (3,0) 定角主块被拉出角落
      if (dir === "RIGHT" && this.wouldBreakBottomLeftAnchor(this.tiles, sim.tiles)) {
        totalVal -= 450000;
      }

      if (useTwoPly) {
        const nextBest = this.lookaheadSecondPly(sim.tiles);
        totalVal = totalVal * 0.45 + nextBest * 0.55;
      }

      if (totalVal > bestScore) {
        bestScore = totalVal;
        bestDir = dir;
      }
    }

    if (bestDir) return bestDir;

    // 唯有当 DOWN / LEFT / RIGHT 全部锁死时，才被迫走 UP 破局
    const upSim = this.simulateMove(this.tiles, "UP");
    return upSim.moved ? "UP" : null;
  }

  private wouldBreakBottomLeftAnchor(before: Tile[], after: Tile[]): boolean {
    let maxVal = 0;
    for (const t of before) {
      if (t.val > maxVal) maxVal = t.val;
    }
    if (maxVal < 16) return false;

    const wasAnchored = before.some((t) => t.r === 3 && t.c === 0 && t.val === maxVal);
    if (!wasAnchored) return false;

    const stillAnchored = after.some((t) => t.r === 3 && t.c === 0 && t.val >= maxVal);
    return !stillAnchored;
  }

  private lookaheadSecondPly(tilesAfterFirst: Tile[]): number {
    // 抽样最多 3 个空位模拟生成 2，评估下一步最佳应对
    const occupied = new Set(tilesAfterFirst.map((t) => `${t.r},${t.c}`));
    const empties: { r: number; c: number }[] = [];
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        if (!occupied.has(`${r},${c}`)) empties.push({ r, c });
      }
    }

    if (empties.length === 0) {
      return this.evaluateBoard(tilesAfterFirst, []);
    }

    const sampled = empties.slice(0, 3);
    let sumScore = 0;

    for (const spot of sampled) {
      const withSpawn: Tile[] = [
        ...tilesAfterFirst.map((t) => ({ ...t })),
        { id: 88888, r: spot.r, c: spot.c, val: 2 },
      ];

      let bestReply = -Infinity;
      for (const dir of ["DOWN", "LEFT", "RIGHT"] as MoveDirection[]) {
        const sim2 = this.simulateMove(withSpawn, dir);
        if (sim2.moved) {
          let sc = this.evaluateBoard(sim2.tiles, sim2.mergedValues);
          if (dir === "RIGHT" && this.wouldBreakBottomLeftAnchor(withSpawn, sim2.tiles)) {
            sc -= 450000;
          }
          if (sc > bestReply) bestReply = sc;
        }
      }

      if (bestReply === -Infinity) {
        bestReply = this.evaluateBoard(withSpawn, []) - 200000;
      }
      sumScore += bestReply;
    }

    return sumScore / sampled.length;
  }

  private simulateMove(
    sourceTiles: Tile[],
    dir: MoveDirection
  ): { moved: boolean; tiles: Tile[]; mergedValues: number[] } {
    const isVertical = dir === "UP" || dir === "DOWN";
    const isReverse = dir === "DOWN" || dir === "RIGHT";
    const cloned: Tile[] = sourceTiles.map((t) => ({ ...t }));
    let moved = false;
    const toRemove: Tile[] = [];
    const toSpawn: Tile[] = [];
    const mergedValues: number[] = [];
    let tempId = 90000;

    for (let i = 0; i < 4; i++) {
      const line = cloned.filter((t) => (isVertical ? t.c === i : t.r === i));
      const res = processLine(line, isReverse, isVertical, i, () => tempId++, () => {});
      if (res.moved) {
        moved = true;
        toRemove.push(...res.toRemove);
        toSpawn.push(...res.toSpawn);
        mergedValues.push(...res.mergedValues);
      }
    }

    if (!moved) return { moved: false, tiles: [], mergedValues: [] };

    const removeSet = new Set(toRemove.map((t) => t.id));
    const nextTiles = cloned.filter((t) => !removeSet.has(t.id));
    nextTiles.push(...toSpawn);
    return { moved: true, tiles: nextTiles, mergedValues };
  }

  /**
   * 综合估值函数：
   * 1. 蛇形梯度矩阵 (左下角 (3,0) 权重最高，引导数字沿底排蛇形排列)
   * 2. 相邻平滑度与成对奖励 (相邻格数值相同或差1阶给予奖励，极大提升连续合成率)
   * 3. 行列单调性 (保证从左到右、从下到上严格递减，防止小数字卡死在大数字中间)
   * 4. 战术狙击加权 (若当步合成能精准命中玩家场上的方块，额外加分)
   */
  private evaluateBoard(tiles: Tile[], mergedValues: number[]): number {
    const SNAKE_WEIGHTS = [
      [2, 4, 8, 16],
      [256, 128, 64, 32],
      [512, 1024, 2048, 4096],
      [65536, 32768, 16384, 8192],
    ];

    const grid: number[][] = [
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
    ];

    let maxVal = 0;
    let maxR = 0;
    let maxC = 0;

    for (const t of tiles) {
      if (t.r >= 0 && t.r < 4 && t.c >= 0 && t.c < 4) {
        grid[t.r][t.c] = t.val;
        if (t.val > maxVal) {
          maxVal = t.val;
          maxR = t.r;
          maxC = t.c;
        }
      }
    }

    let score = 0;

    // 1. 蛇形位置权重
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        if (grid[r][c] > 0) {
          score += grid[r][c] * SNAKE_WEIGHTS[r][c];
        }
      }
    }

    // 2. 最大块定角奖励（若最大块稳居左下角 (3,0)，给予高额稳态加分）
    if (maxR === 3 && maxC === 0) {
      score += maxVal * 35000;
    } else {
      score -= maxVal * 25000;
    }

    // 3. 空位二次方奖励（越少空位越优先消块腾空间）
    const emptyCount = 16 - tiles.length;
    score += emptyCount * emptyCount * 420;

    // 4. 平滑度与相邻等值奖励（鼓励把相同数字排在一起准备下一步合成）
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        const v = grid[r][c];
        if (v === 0) continue;
        const logV = Math.log2(v);

        if (c < 3 && grid[r][c + 1] > 0) {
          const rightV = grid[r][c + 1];
          if (v === rightV) {
            score += v * 1200; // 相邻等值对：下一步可直接合并！
          } else {
            score -= Math.abs(logV - Math.log2(rightV)) * v * 90;
          }
        }
        if (r < 3 && grid[r + 1][c] > 0) {
          const downV = grid[r + 1][c];
          if (v === downV) {
            score += v * 1200;
          } else {
            score -= Math.abs(logV - Math.log2(downV)) * v * 90;
          }
        }
      }
    }

    // 5. 战术狙击洞察：若本步合出了 >= 64 的方块，且恰好能命中玩家场上的对应数字，优先执行！
    const isLeading = this.score >= this.playerScore;
    for (const mv of mergedValues) {
      if (mv >= 64) {
        const targetVal = isLeading ? Math.floor(mv / 2) : mv;
        if ((this.playerTileCounts.get(targetVal) || 0) > 0) {
          score += targetVal * 8000;
        }
      }
    }

    return score;
  }
}

